import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, Zap, PenTool as Tool, Brain, MessageCircle, Settings, X } from 'lucide-react';

interface RealAgentInterfaceProps {
  agent: any;
  isOpen: boolean;
  onClose: () => void;
}

interface Message {
  id: string;
  type: 'user' | 'agent' | 'system' | 'tool';
  content: string;
  timestamp: Date;
  metadata?: any;
}

export function RealAgentInterface({ agent, isOpen, onClose }: RealAgentInterfaceProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      // Initialize session
      const newSessionId = `real_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setSessionId(newSessionId);
      
      // Add welcome message
      const welcomeMessage: Message = {
        id: '1',
        type: 'system',
        content: `🤖 **${agent.name}** - Real AI Agent\n\n${agent.description}\n\n**Capabilities:**\n${(agent.metadata?.capabilities || []).map((cap: string) => `• ${cap.replace('_', ' ')}`).join('\n')}\n\n**Tools Available:** ${agent.metadata?.tools?.length || 0}\n**Memory:** ${agent.metadata?.memory_enabled ? 'Enabled' : 'Disabled'}\n**A2A Communication:** ${agent.metadata?.a2a_enabled ? 'Enabled' : 'Disabled'}`,
        timestamp: new Date()
      };

      const agentIntro: Message = {
        id: '2',
        type: 'agent',
        content: `Hello! I'm ${agent.name}. ${agent.metadata?.personality || 'I\'m here to help you with various tasks using my available tools and capabilities.'} What would you like me to help you with today?`,
        timestamp: new Date()
      };

      setMessages([welcomeMessage, agentIntro]);
      
      // Focus input
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      // Reset when closed
      setMessages([]);
      setInputMessage('');
      setSessionId('');
    }
  }, [isOpen, agent]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: inputMessage.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/agent-runtime`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            agent_id: agent.id,
            message: userMessage.content,
            session_id: sessionId,
            user_id: null // Add user ID if authenticated
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Agent request failed: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        const agentMessage: Message = {
          id: (Date.now() + 1).toString(),
          type: 'agent',
          content: data.response,
          timestamp: new Date(),
          metadata: data.metadata
        };

        setMessages(prev => [...prev, agentMessage]);
      } else {
        throw new Error(data.error || 'Agent request failed');
      }
    } catch (error) {
      console.error('Agent error:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'agent',
        content: "I apologize, but I encountered an error while processing your request. Please try again.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const quickActions = [
    "What are your capabilities?",
    "Show me what tools you have",
    "Help me with a calculation",
    "Search for information about AI",
    "What can you remember about our conversation?"
  ];

  if (!isOpen) return null;

  const isRealAgent = agent.metadata?.is_real;
  const toolsCount = agent.metadata?.tools?.length || 0;
  const memoryEnabled = agent.metadata?.memory_enabled;
  const a2aEnabled = agent.metadata?.a2a_enabled;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-green-600 to-blue-600 text-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Bot className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">{agent.name}</h2>
              <p className="text-sm opacity-90">Real AI Agent with Tools & Memory</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 px-2 py-1 bg-white/20 rounded-lg text-xs">
              <Zap className="w-3 h-3" />
              Live
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Agent Status */}
        <div className="p-4 bg-green-50 border-b border-green-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1">
                <Tool className="w-4 h-4 text-green-600" />
                <span className="text-green-800">{toolsCount} Tools</span>
              </div>
              <div className="flex items-center gap-1">
                <Brain className="w-4 h-4 text-blue-600" />
                <span className="text-blue-800">Memory: {memoryEnabled ? 'On' : 'Off'}</span>
              </div>
              <div className="flex items-center gap-1">
                <MessageCircle className="w-4 h-4 text-purple-600" />
                <span className="text-purple-800">A2A: {a2aEnabled ? 'Enabled' : 'Disabled'}</span>
              </div>
            </div>
            <div className="text-xs text-green-600">
              Session: {sessionId.slice(-8)}
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex gap-3 ${
                message.type === 'user' ? 'justify-end' : 'justify-start'
              }`}
            >
              {message.type !== 'user' && (
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  message.type === 'agent' 
                    ? 'bg-gradient-to-r from-green-600 to-blue-600 text-white'
                    : message.type === 'tool'
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-500 text-white'
                }`}>
                  {message.type === 'agent' ? (
                    <Bot className="w-4 h-4" />
                  ) : message.type === 'tool' ? (
                    <Tool className="w-4 h-4" />
                  ) : (
                    <Settings className="w-4 h-4" />
                  )}
                </div>
              )}
              
              <div
                className={`max-w-[80%] p-3 rounded-2xl ${
                  message.type === 'user'
                    ? 'bg-blue-600 text-white'
                    : message.type === 'agent'
                    ? 'bg-green-50 text-gray-900 border border-green-200'
                    : message.type === 'tool'
                    ? 'bg-orange-50 text-orange-900 border border-orange-200'
                    : 'bg-gray-50 text-gray-900 border border-gray-200'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                <div className="flex items-center justify-between mt-2">
                  <p className={`text-xs ${
                    message.type === 'user' 
                      ? 'text-blue-100' 
                      : message.type === 'agent'
                      ? 'text-green-600'
                      : message.type === 'tool'
                      ? 'text-orange-600'
                      : 'text-gray-500'
                  }`}>
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                  {message.metadata?.tools_used > 0 && (
                    <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">
                      {message.metadata.tools_used} tools used
                    </span>
                  )}
                </div>
              </div>
              
              {message.type === 'user' && (
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-white" />
                </div>
              )}
            </div>
          ))}
          
          {isLoading && (
            <div className="flex gap-3 justify-start">
              <div className="w-8 h-8 bg-gradient-to-r from-green-600 to-blue-600 rounded-full flex items-center justify-center">
                <Bot className="w-4 h-4 text-white" />
              </div>
              <div className="bg-green-50 border border-green-200 p-3 rounded-2xl">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  <span className="text-sm text-green-700 ml-2">
                    Processing with {toolsCount} tools...
                  </span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Actions */}
        {messages.length <= 2 && (
          <div className="px-4 pb-2">
            <p className="text-xs text-gray-500 mb-2">Quick actions:</p>
            <div className="flex flex-wrap gap-2">
              {quickActions.map((action, index) => (
                <button
                  key={index}
                  onClick={() => setInputMessage(action)}
                  className="text-xs px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-gray-700"
                >
                  {action}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <div className="p-4 border-t border-gray-200">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={`Message ${agent.name}...`}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              disabled={isLoading}
            />
            <button
              onClick={sendMessage}
              disabled={!inputMessage.trim() || isLoading}
              className="px-4 py-2 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-lg hover:from-green-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs text-gray-500">
              Real agent with {toolsCount} tools • Memory {memoryEnabled ? 'enabled' : 'disabled'}
            </p>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <MessageCircle className="w-3 h-3" />
              {messages.filter(m => m.type !== 'system').length} messages
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}